import { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";

const MenuItem = ({ as: Component = "button", className = "", children, ...props }) => (
  <Component
    type={Component === "button" ? "button" : undefined}
    className={`dropdown-item ${className}`.trim()}
    {...props}
  >
    {children}
  </Component>
);

export default function StatusHandler({
  isLoading,
  item,
  onClick,
  redirectUrl,
  view,
  view2,
  status,
  active,
  suspend,
  approval,
  edit,
  viewUrl,
  onDelete,
  onEdit,
}) {
  const [open, setOpen] = useState(false);
  const [menuReady, setMenuReady] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});
  const wrapRef = useRef(null);
  const menuRef = useRef(null);

  const closeMenu = useCallback(() => {
    setOpen(false);
    setMenuReady(false);
  }, []);

  const updateMenuPosition = useCallback(() => {
    const btn = wrapRef.current?.querySelector(".ellipsis-btn");
    const menu = menuRef.current;
    if (!btn) return;

    const rect = btn.getBoundingClientRect();
    const menuHeight = menu?.offsetHeight || 120;
    const menuWidth = menu?.offsetWidth || 152;
    const padding = 12;
    const gap = 6;

    const spaceBelow = window.innerHeight - rect.bottom - padding;
    const openUp = spaceBelow < menuHeight && rect.top > menuHeight + padding;

    let top = openUp ? rect.top - gap - menuHeight : rect.bottom + gap;
    let left = rect.right;

    left = Math.min(
      Math.max(menuWidth + padding, left),
      window.innerWidth - padding
    );

    top = Math.max(
      padding,
      Math.min(top, window.innerHeight - menuHeight - padding)
    );

    setMenuStyle({
      position: "fixed",
      top,
      left,
      transform: "translateX(-100%)",
      zIndex: 1060,
      visibility: "visible",
      opacity: 1,
    });
    setMenuReady(true);
  }, []);

  const toggleMenu = () => {
    if (open) {
      closeMenu();
      return;
    }
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return undefined;

    updateMenuPosition();
    requestAnimationFrame(updateMenuPosition);

    const handleOutside = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        closeMenu();
      }
    };

    const handleReposition = () => updateMenuPosition();

    document.addEventListener("mousedown", handleOutside);
    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);

    return () => {
      document.removeEventListener("mousedown", handleOutside);
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
    };
  }, [open, closeMenu, updateMenuPosition]);

  if (isLoading) {
    return <i className="fas fa-circle-notch fa-spin" />;
  }

  return (
    <div className="table-action-dropdown" ref={wrapRef}>
      <button
        type="button"
        className="btn transparent-btn ellipsis-btn"
        aria-expanded={open}
        onClick={toggleMenu}
      >
        <i className="fa fa-ellipsis-v" />
      </button>

      {open && (
        <div
          ref={menuRef}
          className={`dropdown-menu custom-dropdown show table-action-dropdown__menu${menuReady ? " is-ready" : ""}`}
          style={{
            ...menuStyle,
            visibility: menuReady ? "visible" : "hidden",
            opacity: menuReady ? 1 : 0,
          }}
        >
          {view && (
            <MenuItem as={Link} to={redirectUrl} onClick={closeMenu}>
              <i className="far fa-eye" />
              View
            </MenuItem>
          )}
          {view2 && (
            <MenuItem as={Link} to={viewUrl} onClick={closeMenu}>
              <i className="far fa-eye" />
              View
            </MenuItem>
          )}
          {edit &&
            (redirectUrl ? (
              <MenuItem as={Link} to={redirectUrl} onClick={closeMenu}>
                <i className="fas fa-pen" />
                Edit
              </MenuItem>
            ) : (
              <MenuItem onClick={() => { closeMenu(); onEdit?.(); }}>
                <i className="fas fa-pen" />
                Edit
              </MenuItem>
            ))}
          {status && (
            <MenuItem onClick={() => { closeMenu(); onClick?.(); }}>
              <i
                className={
                  item?.status ? "fas fa-user-times" : "fas fa-user-check"
                }
              />
              {item?.status ? "Inactive" : "Active"}
            </MenuItem>
          )}
          {active && (
            <MenuItem onClick={() => { closeMenu(); onClick?.(); }}>
              <i
                className={
                  item?.active ? "fas fa-toggle-off" : "fas fa-toggle-on"
                }
              />
              {item?.active ? "Inactive" : "Active"}
            </MenuItem>
          )}
          {suspend && (
            <MenuItem onClick={() => { closeMenu(); onClick?.(); }}>
              <i
                className={
                  item?.active ? "fas fa-user-slash" : "fas fa-user-check"
                }
              />
              {item?.active ? "Inactive" : "Active"}
            </MenuItem>
          )}
          {approval && (
            <MenuItem onClick={() => { closeMenu(); onClick?.(); }}>
              <i
                className={
                  item?.requestApprovalStatus === "accepted"
                    ? "fas fa-times"
                    : "fas fa-check"
                }
              />
              {item?.requestApprovalStatus === "accepted" ? "Reject" : "Accept"}
            </MenuItem>
          )}
          {onDelete && (
            <MenuItem
              className="text-danger"
              onClick={() => {
                closeMenu();
                onDelete?.();
              }}
            >
              <i className="fas fa-trash-alt" />
              Delete
            </MenuItem>
          )}
        </div>
      )}
    </div>
  );
}
